/**
 * Reveal-on-scroll.
 *
 * Constants are measured from the Athora reference storefront and reproduced
 * exactly — 0.48s over 16px with a 0.09s stagger, easeOut, fired once at 10%
 * visibility. Changing any of them changes the feel of the whole store, so they
 * live here as named constants rather than being scattered through sections.
 *
 * Mark up a container with `reveal-on-scroll="true"` and its children with
 * `can-reveal` (default direction) or `reveal="top|bottom|left|right"`.
 * `reveal-distance` and `reveal-repeat` on the container override the defaults.
 */
import { animate, inView } from '@theme/motion';
import { prefersReducedMotion } from '@theme/utilities';

const DURATION = 0.48;
const STAGGER = 0.09;
const DISTANCE = 16;
const EASE = 'easeOut';

/**
 * Visibility fraction that fires the reveal. Deliberately low: at 10% the
 * element is barely on screen, so the animation reads as the element arriving
 * rather than as a delayed response to it already being there.
 */
const IN_VIEW_AMOUNT = 0.1;

/**
 * The stagger is capped rather than linear. Past the sixth sibling a linear
 * ramp makes the tail of a long grid feel broken; this compresses items 7–9 and
 * flattens everything beyond to a constant 0.72s.
 */
function staggerDelay(index) {
  if (index <= 6) return index * STAGGER;
  if (index < 10) return 6 * STAGGER + index * 0.2 * STAGGER;
  return 8 * STAGGER;
}

const isRTL = () => document.documentElement.dir === 'rtl';

/**
 * Physical direction, honouring RTL and any vertical-only ancestor.
 *
 * RTL mirroring applies only to a direction the author wrote. `reveal="left"`
 * means "from the leading edge", so it becomes right in Arabic. The container
 * fallback is already resolved against direction, and mirroring it a second
 * time would cancel the first — which is exactly the bug this guards.
 */
function resolveDirection(element, fallback, vertical) {
  const authored = element.getAttribute('reveal');
  let dir = String(authored ?? fallback).trim().toLowerCase();

  let mirror = Boolean(authored);
  if (!['top', 'bottom', 'left', 'right'].includes(dir)) {
    dir = fallback;
    mirror = false;
  }

  // A vertical container has no horizontal room to travel through.
  if (vertical && (dir === 'left' || dir === 'right')) return 'top';

  if (mirror && isRTL()) {
    if (dir === 'left') return 'right';
    if (dir === 'right') return 'left';
  }

  return dir;
}

function offsetFor(direction, distance) {
  switch (direction) {
    case 'top':
      return { x: 0, y: -distance };
    case 'bottom':
      return { x: 0, y: distance };
    case 'right':
      return { x: distance, y: 0 };
    case 'left':
    default:
      return { x: -distance, y: 0 };
  }
}

export class RevealOnScroll {
  #stopFns = [];

  constructor(container) {
    this.container = container;
    this.repeat = container.hasAttribute('reveal-repeat');

    const parsed = Number.parseFloat(container.getAttribute('reveal-distance'));
    this.distance = Number.isFinite(parsed) ? parsed : DISTANCE;

    this.fallbackDirection = container.getAttribute('default-reveal') || (isRTL() ? 'right' : 'left');

    // Sliders run their own entrance; revealing their slides as well would
    // double-animate and fight the carousel's transform.
    this.elements = Array.from(container.querySelectorAll('[reveal], [can-reveal]')).filter(
      (element) => !RevealOnScroll.#isSliderOwned(element)
    );

    this.#init();
  }

  static #isSliderOwned(element) {
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
      const tag = parent.tagName?.toLowerCase();
      if (tag === 'nuvrya-slider-reveal' || tag === 'nuvrya-reveal-items') return true;
      if (parent.classList.contains('nuvrya-reveal--disabled')) return true;
    }
    return false;
  }

  static #isVertical(element) {
    return Boolean(element.closest('.nuvrya-reveal--vertical'));
  }

  #setInitialState(element) {
    const { x, y } = element._nuvryaRevealOffset;
    element.style.opacity = '0';
    element.style.transform = `translate(${x}px, ${y}px)`;
  }

  #reveal(element, index) {
    const { x, y } = element._nuvryaRevealOffset;

    // A disabled control still arrives, it just arrives dimmed.
    const target = element.hasAttribute('aria-disabled') ? 0.5 : 1;

    animate(
      element,
      { opacity: [0, target], x: [x, 0], y: [y, 0] },
      { duration: DURATION, delay: staggerDelay(index), ease: EASE, fill: 'forwards' }
    );
  }

  #init() {
    this.elements.forEach((element, index) => {
      // Resolve direction once and hang it on the element. Reading it twice —
      // once to place the element and again to animate it — lets the two
      // disagree if anything changes `dir` between load and first paint, and the
      // element then jumps instead of travelling.
      element._nuvryaRevealOffset = offsetFor(
        resolveDirection(element, this.fallbackDirection, RevealOnScroll.#isVertical(element)),
        this.distance
      );

      this.#setInitialState(element);

      const stop = inView(
        element,
        () => {
          this.#reveal(element, index);
          if (this.repeat) return () => this.#setInitialState(element);
          return undefined;
        },
        { amount: IN_VIEW_AMOUNT }
      );

      this.#stopFns.push(stop);
    });
  }

  destroy() {
    for (const stop of this.#stopFns) {
      try {
        stop?.();
      } catch {
        // An observer torn down with its section is not an error worth surfacing.
      }
    }
    this.#stopFns = [];
  }
}

/** (Re)binds one container, replacing any instance already attached to it. */
export function initReveal(container) {
  container._nuvryaReveal?.destroy();
  container._nuvryaReveal = new RevealOnScroll(container);
}

export function initAllReveals(root = document) {
  if (prefersReducedMotion()) return;
  for (const container of root.querySelectorAll('[reveal-on-scroll="true"]')) initReveal(container);
}

/* ------------------------------------------------------------------
 * Reveal coverage
 *
 * The engine above is an exact reproduction of the reference's, but an engine
 * only animates what is marked, and the marking had been done by hand section
 * by section. Measured on the live pages: the reference carries 234 reveal
 * elements on its homepage; this theme carried 26, and thirteen of the twenty-two
 * content sections carried none at all — roughly ten thousand pixels of page
 * where content simply appeared.
 *
 * That single gap is why the store read as flat and why the sections felt cut
 * apart from one another: the cascade that ties one section to the next was
 * running on a fifth of the page.
 *
 * Rather than hand-mark thirteen more sections and every future one, an unmarked
 * section gets its content children marked here. A section that already declares
 * its own reveal markup is left completely alone — the hand-written choreography
 * always wins.
 * ---------------------------------------------------------------- */

/**
 * Elements that run their own entrance or their own scroll-driven transform.
 * Marking inside them means two systems writing the same transform, which shows
 * up as a stutter rather than as an error.
 */
const SELF_ANIMATED = [
  'nuvrya-marquee',
  'nuvrya-effect-img',
  'nuvrya-shrink-media',
  'nuvrya-hero-collections',
  'nuvrya-slider-reveal',
  'nuvrya-reveal-items',
  'slideshow-component',
  '[eff]',
  '.nuvrya-reveal--disabled',
].join(',');

/** Sections whose first paint is their own composition, not a cascade. */
const SKIP_SECTIONS = [
  'nuvrya-campaign-hero',
  'slideshow',
  'header',
  'footer',
  'cart-drawer',
  'announcement',
].join('|');

/** A child small enough to be chrome rather than content is not worth marking. */
const MIN_CHILD_SIZE = 24;

/** The reference tops out around ten items in one cascade; past that the
 *  stagger is flat anyway, so marking more only costs observers. */
const MAX_PER_CONTAINER = 10;

function isMarkable(element) {
  if (element.matches(SELF_ANIMATED) || element.closest(SELF_ANIMATED)) return false;
  if (element.hasAttribute('can-reveal') || element.hasAttribute('reveal')) return false;

  const rect = element.getBoundingClientRect();
  if (rect.width < MIN_CHILD_SIZE || rect.height < MIN_CHILD_SIZE) return false;

  // A child that is itself the full section is a wrapper, not an item.
  return true;
}

/**
 * The container worth cascading is the deepest wrapper that still holds several
 * siblings. Walking down past single-child wrappers is what stops a whole
 * section animating as one block — which is a fade, not a cascade.
 */
function contentContainer(section) {
  let node = section;
  for (let depth = 0; depth < 4; depth += 1) {
    const children = [...node.children].filter((child) => {
      const rect = child.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    if (children.length === 0) return null;
    if (children.length > 1) return node;
    node = children[0];
  }
  return node;
}

function autoMarkSection(section) {
  if (section.querySelector('[can-reveal], [reveal]')) return;
  if (new RegExp(SKIP_SECTIONS).test(section.id)) return;
  if (section.matches(SELF_ANIMATED)) return;

  const container = contentContainer(section);
  if (!container) return;

  const marked = [...container.children].filter(isMarkable).slice(0, MAX_PER_CONTAINER);
  if (marked.length === 0) return;

  for (const element of marked) element.setAttribute('can-reveal', '');
  container.setAttribute('reveal-on-scroll', 'true');
  // Content arrives from the leading edge by default, the way the reference
  // does it; a section that wants otherwise sets `default-reveal` in Liquid.
  return container;
}

/**
 * A rail or a grid: several siblings of the same kind, side by side. These are
 * where a cascade actually reads — a row of four cards arriving one after
 * another is the difference between a page that moves and a page that fades.
 * Marking only the section's top-level children gave two or three items per
 * section and left every card inside them arriving at once.
 */
function repeatedGroups(section) {
  const groups = [];

  for (const node of section.querySelectorAll('*')) {
    const children = [...node.children];
    if (children.length < 3 || children.length > 24) continue;
    if (node.matches(SELF_ANIMATED) || node.closest(SELF_ANIMATED)) continue;
    if (node.querySelector('[can-reveal], [reveal]')) continue;

    // "Same kind" is the first class token, falling back to the tag name. A
    // list of <li> with no classes is as much a rail as a row of __card divs.
    const kind = (child) => String(child.className || '').split(/\s+/)[0] || child.tagName;
    const first = kind(children[0]);
    if (!children.every((child) => kind(child) === first)) continue;
    if (!children.every(isMarkable)) continue;

    groups.push({ node, children });
  }

  // Only the outermost group of any nest: marking a rail and then the contents
  // of each of its cards animates the same pixels twice.
  return groups.filter((group) => !groups.some((other) => other !== group && other.node.contains(group.node)));
}

export function autoMarkReveals(root = document) {
  if (prefersReducedMotion()) return;

  for (const section of root.querySelectorAll('.shopify-section')) {
    if (new RegExp(SKIP_SECTIONS).test(section.id)) continue;

    const container = autoMarkSection(section);
    if (container) initReveal(container);

    for (const { node, children } of repeatedGroups(section)) {
      const marked = children.slice(0, MAX_PER_CONTAINER);
      for (const element of marked) element.setAttribute('can-reveal', '');
      node.setAttribute('reveal-on-scroll', 'true');
      initReveal(node);
    }
  }
}

initAllReveals();
autoMarkReveals();

// The theme editor swaps section markup in and out without a page load.
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initAllReveals(event.target);
    autoMarkReveals(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    for (const container of event.target.querySelectorAll('[reveal-on-scroll="true"]')) {
      container._nuvryaReveal?.destroy();
    }
  });
}
